import { useState, useEffect } from "react";

interface IntakeProgressBarProps {
  currentStep: number;
  totalSteps: number;
  variant?: "default" | "overlay";
}

function getStepPosition(index: number, total: number): number {
  if (total <= 1) return 0;
  return (index / (total - 1)) * 100;
}

export default function IntakeProgressBar({
  currentStep,
  totalSteps,
  variant = "default",
}: IntakeProgressBarProps) {
  const [barPulse, setBarPulse] = useState(false);
  const [checkPop, setCheckPop] = useState(false);

  const fillPercent = getStepPosition(currentStep - 1, totalSteps);
  const displayPercent = Math.round((currentStep / totalSteps) * 100);

  useEffect(() => {
    const t1 = setTimeout(() => setBarPulse(true), 50);
    const t2 = setTimeout(() => setCheckPop(true), 350);
    const t3 = setTimeout(() => setBarPulse(false), 650);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [currentStep]);

  const isOverlay = variant === "overlay";

  const stepNodes = Array.from({ length: totalSteps }, (_, i) => {
    const stepNum = i + 1;
    const isCompleted = stepNum < currentStep;
    const isCurrent = stepNum === currentStep;
    const left = getStepPosition(i, totalSteps);

    return (
      <div
        key={i}
        className="absolute top-1/2 -translate-y-1/2 z-10"
        style={{ left: `${left}%`, transform: `translate(-50%, -50%)` }}
      >
        <div
          className={`flex items-center justify-center rounded-full transition-all duration-500 ${
            isCompleted
              ? `w-[22px] h-[22px] bg-primary-500 border-2 border-primary-500 ${checkPop ? "animate-checkPop" : ""}`
              : isCurrent
                ? `w-[22px] h-[22px] bg-primary-500 border-2 border-primary-500 shadow-[0_0_0_4px_rgba(0,0,0,0.06)] animate-nodePulse`
                : isOverlay
                  ? "w-[14px] h-[14px] bg-white/40 border border-white/50"
                  : "w-[14px] h-[14px] bg-background-200 border border-background-300"
          }`}
        >
          {isCompleted && (
            <span
              className={`w-3 h-3 flex items-center justify-center text-background-50 ${checkPop ? "animate-checkPopIcon" : ""}`}
            >
              <i className="ri-check-line text-[10px]"></i>
            </span>
          )}
        </div>
      </div>
    );
  });

  return (
    <div className="flex items-center gap-3">
      <span
        className={`text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${
          isOverlay ? "text-background-50/80" : "text-foreground-400"
        }`}
      >
        Step {currentStep} of {totalSteps}
      </span>

      <div className="flex-1 relative h-7 flex items-center">
        {/* Background track */}
        <div
          className={`absolute left-0 right-0 h-1.5 rounded-full ${
            isOverlay ? "bg-background-50/30" : "bg-background-100"
          }`}
        />

        {/* Filled bar */}
        <div
          className={`absolute left-0 h-1.5 rounded-full transition-all duration-700 ease-out ${
            isOverlay ? "bg-accent-400" : "bg-primary-500"
          } ${barPulse ? "animate-barPulse" : ""}`}
          style={{ width: `${fillPercent}%` }}
        />

        {/* Step nodes */}
        {stepNodes}
      </div>

      <span
        className={`text-[10px] font-bold tabular-nums whitespace-nowrap ${
          isOverlay ? "text-background-50" : "text-foreground-600"
        }`}
      >
        {displayPercent}%
      </span>
    </div>
  );
}
